import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, Clock, MapPin } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { fetchRelated } from "../lib/api";
import { resolveMediaUrl } from "../lib/media";
import { postUrl } from "../lib/urls";
import SEO from "../components/SEO";
import ShareButtons from "../components/ShareButtons";
import ReadersNotebook from "../components/ReadersNotebook";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getPost, getCategory, getTag, posts } = useContent();
  const post = getPost(slug);
  const [related, setRelated] = useState([]);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  // Redirect /post/:slug → canonical /<country>/<slug> once the post is loaded.
  useEffect(() => {
    if (!post) return;
    const canonical = postUrl(post);
    if (window.location.pathname !== canonical && canonical !== "/") {
      navigate(canonical, { replace: true });
    }
  }, [post, navigate]);

  useEffect(() => {
    if (!post) return;
    fetchRelated(slug, 3)
      .then(setRelated)
      .catch(() => {
        // fallback local
        setRelated(posts.filter((p) => p.slug !== slug).slice(0, 3));
      });
  }, [slug, post, posts]);

  if (!post && posts.length > 0) return <Navigate to="/destinations" replace />;
  if (!post) return null;

  return (
    <main data-testid="blog-post-page" className="bg-[#FDFBF7]">
      <SEO
        title={post.title}
        description={post.excerpt}
        image={resolveMediaUrl(post.hero)}
        path={postUrl(post)}
        type="article"
        article={{
          title: post.title,
          date: post.date,
          section: post.categories?.[0] || "",
          keywords: (post.tags || []).join(", "),
        }}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Destinations", path: "/destinations" },
          ...(post.categories?.[0]
            ? [{ name: getCategory(post.categories[0])?.name || post.categories[0], path: `/category/${post.categories[0]}` }]
            : []),
          { name: post.title, path: postUrl(post) },
        ]}
      />
      <motion.div style={{ scaleX: scrollYProgress }} className="fixed top-[72px] left-0 right-0 h-[2px] bg-[#9c4122] origin-left z-40" />

      <section className="relative h-[90vh] min-h-[560px] w-full overflow-hidden bg-stone-900">
        <img src={resolveMediaUrl(post.hero)} alt={post.title} fetchpriority="high" className="absolute inset-0 w-full h-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/80" />
        <div className="relative z-10 h-full max-w-5xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16 lg:pb-24 text-white">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-[11px] tracking-[0.3em] uppercase font-semibold text-white/80 flex items-center gap-3">
            <span className="h-px w-10 bg-white/60" />
            {post.kicker}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }} className="font-serif text-4xl sm:text-6xl lg:text-7xl font-light leading-[1] mt-6 max-w-4xl tracking-tight">
            {post.title}
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 1 }} className="mt-8 flex flex-wrap items-center gap-6 text-xs tracking-widest uppercase text-white/80">
            <span className="flex items-center gap-2"><Clock size={14} /> {post.readTime} min read</span>
            <span className="flex items-center gap-2"><MapPin size={14} /> {post.categories?.[0] ? getCategory(post.categories[0])?.name : ""}</span>
            <span>{new Date(post.date).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
          </motion.div>
        </div>
      </section>

      <article className="max-w-2xl mx-auto px-6 lg:px-0 py-20 lg:py-28">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-stone-800 text-[#FDFBF7] flex items-center justify-center font-serif text-sm">AP</div>
            <div>
              <p className="text-sm font-medium text-stone-900">Ashwini Parulkar</p>
              <p className="text-xs text-stone-500 tracking-widest uppercase">Writer & Wanderer</p>
            </div>
          </div>
          <ShareButtons
            title={post.title}
            description={post.excerpt}
            compact
          />
        </div>

        <p className="font-serif text-2xl sm:text-3xl font-light italic leading-snug text-stone-800 border-l-2 border-[#9c4122] pl-6">
          {post.excerpt}
        </p>

        <div className="mt-12 space-y-8 text-[17px] sm:text-[18px] leading-[1.75] text-stone-800 font-normal">
          {post.body?.map((block, i) => {
            if (block.type === "pullquote") {
              return (
                <motion.blockquote key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="my-14 font-serif text-3xl sm:text-4xl italic font-light leading-[1.2] text-stone-900 text-center px-4 py-8 border-y border-stone-300">
                  "{block.text}"
                </motion.blockquote>
              );
            }
            return (
              <motion.p key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}>
                {i === 0 && <span className="font-serif text-5xl sm:text-6xl float-left mr-3 mt-1 leading-[0.8] text-[#9c4122]">{block.text.charAt(0)}</span>}
                {i === 0 ? block.text.slice(1) : block.text}
              </motion.p>
            );
          })}
        </div>

        {/* Images gallery if present */}
        {post.images?.length > 0 && (
          <div data-testid="post-images" className="mt-16 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {post.images.map((src, i) => (
              <img key={i} src={src} alt={`${post.title} — photo ${i+1}`} loading="lazy" className="w-full aspect-square object-cover" />
            ))}
          </div>
        )}

        {/* Videos if present */}
        {post.videos?.length > 0 && (
          <div data-testid="post-videos" className="mt-12 space-y-6">
            {post.videos.map((src, i) => (
              <video key={i} src={src} controls className="w-full aspect-video bg-black" />
            ))}
          </div>
        )}

        {/* Article sign-off — elegant Globezing stamp */}
        <div data-testid="post-signoff" className="mt-20 flex flex-col items-center gap-3">
          <img
            src="/globezing-logo-256.png"
            srcSet="/globezing-logo-256.png 1x, /globezing-logo-512.png 2x"
            alt="Globezing"
            width="56"
            height="56"
            loading="lazy"
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-contain opacity-80"
          />
          <p className="font-serif italic text-stone-600 text-sm tracking-wide">
            Think Global · Live Local
          </p>
          <p className="text-[10px] tracking-[0.35em] uppercase text-stone-400">
            Postpone Nothing
          </p>
        </div>

        <div className="mt-16 pt-10 border-t border-stone-300">
          {post.categories?.length > 0 && (
            <div data-testid="post-categories" className="mb-6">
              <p className="kicker text-stone-500 mb-3">Filed under</p>
              <div className="flex flex-wrap gap-2">
                {post.categories.map((s) => {
                  const cat = getCategory(s);
                  if (!cat) return null;
                  return (
                    <Link key={s} to={`/category/${s}`} data-testid={`post-category-${s}`} className="px-3 py-1.5 text-[12px] uppercase tracking-[0.15em] font-semibold bg-stone-900 text-[#FDFBF7] hover:bg-[#9c4122] transition-colors">
                      {cat.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
          {post.tags?.length > 0 && (
            <div data-testid="post-tags" className="mb-8">
              <p className="kicker text-stone-500 mb-3">Tagged</p>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((s) => {
                  const t = getTag(s);
                  if (!t) return null;
                  return (
                    <Link key={s} to={`/tag/${s}`} data-testid={`post-tag-${s}`} className="px-3 py-1.5 text-[12px] tracking-wide rounded-full border border-stone-300 text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors">
                      {t.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between pt-4 gap-4 flex-wrap">
            <Link to="/destinations" data-testid="back-to-destinations" className="inline-flex items-center gap-2 text-sm tracking-widest uppercase text-stone-900 link-underline">
              <ArrowLeft size={16} /> All destinations
            </Link>
            <ShareButtons title={post.title} description={post.excerpt} />
          </div>
        </div>
      </article>

      <ReadersNotebook post={post} />

      {related.length > 0 && (
        <section data-testid="related-posts" className="bg-stone-50 border-t border-stone-200 py-20 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <p className="kicker">Keep Reading</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light mt-3 text-stone-900">Next chapters.</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-10 mt-12">
              {related.map((p) => (
                <Link key={p.slug} to={postUrl(p)} data-testid={`related-card-${p.slug}`} className="group block">
                  <div className="relative aspect-[4/5] overflow-hidden bg-stone-200">
                    <img src={resolveMediaUrl(p.hero)} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  </div>
                  <div className="mt-4">
                    <p className="kicker text-stone-500">{p.kicker}</p>
                    <h3 className="font-serif text-xl sm:text-2xl font-normal leading-snug mt-2 text-stone-900 group-hover:text-[#9c4122] transition-colors">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
